// AdminSettings.tsx
import React, { FunctionComponent, useState, useEffect, useContext, useRef } from "react";
import styles from "./AdminSettings.module.css";
import { Button, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { useQuery, useMutation, useApolloClient } from "@apollo/client";
import {
  GET_MARKETPLACES,
  UPDATE_MARKETPLACE_FEE,
  TOGGLE_MARKETPLACE,
  ADD_MARKETPLACE,
  GET_EMAIL_CAMPAIGN_USERS,
  GET_PUSH_NOTIFICATION_USERS,
  GET_CAMPAIGN_FILTER_USERS,
  GET_CAMPAIGN_USERS_BY_IDS,
} from "./api/AdminSettingsQueries";
import CampaignRecipientFilterPanel from "./CampaignRecipientFilterPanel";
import {
  applyCampaignUserScope,
  buildTicketFilterQueryVariables,
  DEFAULT_CAMPAIGN_RECIPIENT_FILTERS,
  getTicketFilterQuery,
  getTicketRowsFromQueryResult,
  hasActiveCampaignRecipientFilters,
  ticketRowsToUserIds,
  userMatchesTeamFilter,
  type CampaignRecipientFilterState,
} from "./campaignRecipientFilters";
import ToasterWithMessage from "../../components/shared-components/toaster";
import { MARKETPLACE_FEE_PERCENT } from "../../constants/marketplace-fees";
import { globalContext } from "../../Provider/AppProvider";
import { roles } from "../../helper";
import { config } from "../../config/config";
import { GET_ALL_AFFILIATES, GET_AVAILABLE_TEAMS_FOR_USER, GET_USERS_LIST, GET_USERS_PREFERENCE_TEAMS } from "../Users/api/UsersQuery";
import { GET_SALES_REPS_BY_EMAIL } from "../Salesrep/api/SalesrepQuery";

/** Format UTC/ISO timestamp for `<input type="datetime-local">` in the user's local timezone. */
const toDateTimeLocalInputValue = (value?: string | null): string => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** Earliest selectable datetime-local value (local timezone). */
const getMinDateTimeLocalInputValue = (offsetMs = 60 * 1000): string => {
  const date = new Date(Date.now() + offsetMs);
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

interface Marketplace {
  id: string;
  name: string;
  vl_fee_percentage: number;
  active: boolean;
  qa_nets: string;
}

interface EmailCampaignHistoryItem {
  ech_id: string;
  ech_subject: string;
  ech_body?: string;
  ech_header_image?: string;
  ech_footer_image?: string;
  ech_template_type: string;
  ech_recipient_type: string;
  ech_total_recipients: number;
  ech_sent_successfully: number;
  ech_failed: number;
  ech_created_at: string;
  ech_recipient_emails?: string[];
}
interface CampaignUser {
  u_id: string;
  u_full_name?: string | null;
  u_first_name?: string | null;
  u_last_name?: string | null;
  u_email_id: string;
  u_email_enabled?: boolean | null;
  u_affiliate_id?: string | null;
  u_sales_reps_id?: string | null;
  u_notification_enabled?: boolean | null;
  u_device_tokens?: string[] | null;
  u_teams?: string[] | null;
  user_preference?: Array<{
    lc_id?: string | null;
    league_category?: {
      lc_id?: string | null;
      lc_name?: string | null;
      lc_abbrev?: string | null;
    } | null;
  }> | null;
  affiliate?: {
    a_id?: string | null;
    a_email_id?: string | null;
  } | null;
}

interface ScheduledEmailCampaignItem {
  ecs_id: string;
  ecs_subject: string;
  ecs_body?: string;
  ecs_header_image?: string;
  ecs_footer_image?: string;
  ecs_template_type: string;
  ecs_recipient_type: string;
  ecs_status: string;
  ecs_scheduled_at: string;
  ecs_created_at: string;
  ecs_processed_at?: string | null;
  ecs_error?: string | null;
  ecs_recipient_emails?: string[];
}

type EmailSendMode = "now" | "schedule";
type PushFrequency = "one_time" | "daily" | "weekly" | "monthly";
type PushCampaignStatus = "draft" | "active" | "paused" | "processing" | "sent" | "failed";

interface PushNotificationCampaignItem {
  pnc_id: string;
  pnc_title: string;
  pnc_body: string;
  pnc_recipient_type: "all" | "select";
  pnc_selected_user_ids?: string[];
  pnc_selected_emails?: string[];
  pnc_frequency: PushFrequency;
  pnc_status: PushCampaignStatus;
  pnc_scheduled_at?: string | null;
  pnc_next_run_at?: string | null;
  pnc_last_sent_at?: string | null;
  pnc_total_recipients: number;
  pnc_sent_successfully: number;
  pnc_failed: number;
  pnc_error?: string | null;
  pnc_created_at: string;
}

const DEFAULT_USER_FEE_PERCENT = 20;

const computeQaNetLabel = (vlFee: number): string => {
  const qaNet = Math.max(0, DEFAULT_USER_FEE_PERCENT - vlFee);
  return `${Number.isInteger(qaNet) ? qaNet : qaNet.toFixed(1)}%`;
};

const getNumericFee = (value: unknown, fallback = 0): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

// Keep preview aligned with backend campaign email body renderer.
const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const buildCampaignEmailHtml = (
  body: string,
  headerImageUrl?: string,
  footerImageUrl?: string,
): string => {
  const safeBody = escapeHtml(body || "").replace(/\n/g, "<br />");
  const headerHtml = headerImageUrl
    ? `<div style="width:100%; margin-bottom: 16px;"><img src="${headerImageUrl}" alt="Header image" style="width:100%; max-height:240px; object-fit:cover; border-radius: 10px;" /></div>`
    : "";
  const footerHtml = footerImageUrl
    ? `<div style="width:100%; margin-top: 16px;"><img src="${footerImageUrl}" alt="Footer image" style="width:100%; max-height:240px; object-fit:cover; border-radius: 10px;" /></div>`
    : "";

  return `
    <div style="font-family: Arial, sans-serif; max-width: 700px; margin: 0 auto; color: #1f2937;">
      <div style="padding: 24px; border: 1px solid #e5e7eb; border-radius: 12px; background: #ffffff;">
        ${headerHtml}
        <div style="line-height: 1.7; color: #1f2937; font-size: 14px;">
          ${safeBody || "Your email content will appear here..."}
        </div>
        ${footerHtml}
        <hr style="margin: 24px 0; border: 0; border-top: 1px solid #e5e7eb;" />
        <p style="margin: 0; font-size: 12px; color: #6b7280;">
          © 2026 QuickAsyst. All rights reserved.
        </p>
      </div>
    </div>
  `;
};

/** Resolve Node API base URL for local/dev and hosted environments. */
const getNodeApiBaseUrl = (): string => {
  return (
    process.env.REACT_APP_NODE_API_URL ||
    config.node_api_url ||
    ""
  ).replace(/\/+$/, "");
};

const buildNodeApiUrl = (path: string): string => {
  const base = getNodeApiBaseUrl();
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (!base) return normalized;
  return `${base}${normalized}`;
};

const getCampaignUserLabel = (user: CampaignUser): string => {
  const fullName = (user.u_full_name || "").trim();
  if (fullName) return fullName;
  const firstLastName = `${user.u_first_name || ""} ${user.u_last_name || ""}`.trim();
  return firstLastName || user.u_email_id;
};

const AdminSettings: FunctionComponent = () => {
  const { globalState } = useContext<any>(globalContext);
  const userRole = globalState?.profile?.userRole || localStorage.getItem("x-hasura-user-role") || "";
  const isMarketingUser = false;
  const isAffiliateAdmin = userRole === roles.AFFILIATER_ADMIN;
  /** Marketplace VL fees: view-only for affiliate admin */
  const vlFeeReadOnly = isAffiliateAdmin;
  const loggedInEmail = (
    globalState?.profile?.mailId ||
    localStorage.getItem("adminMailId") ||
    ""
  ).toLowerCase().trim();
  // VL Fee States
  const [localFees, setLocalFees] = useState<Record<string, number>>({});
  // Remove localToggles, use loading state for toggles
  const [toggleLoading, setToggleLoading] = useState<Record<string, boolean>>({});
  const { data: marketplacesData, loading: marketplacesLoading, refetch: refetchMarketplaces } = useQuery(GET_MARKETPLACES, { fetchPolicy: 'network-only' });
  const dbMarketplaces: Marketplace[] = Array.isArray(marketplacesData?.marketplaces) ? marketplacesData.marketplaces : [];
  const marketplaces: Marketplace[] = React.useMemo(() => {
    const byName = new Map(dbMarketplaces.map((m) => [m.name.trim().toLowerCase(), m]));
    const merged: Marketplace[] = [...dbMarketplaces];
    for (const [name, fee] of Object.entries(MARKETPLACE_FEE_PERCENT)) {
      if (!byName.has(name.trim().toLowerCase())) {
        merged.push({
          id: `static:${name}`,
          name,
          vl_fee_percentage: fee,
          active: false,
          qa_nets: computeQaNetLabel(fee),
        });
      }
    }
    return merged;
  }, [dbMarketplaces]);

  const [updateFee] = useMutation(UPDATE_MARKETPLACE_FEE);
  const [toggleMarketplace] = useMutation(TOGGLE_MARKETPLACE);
  const [addMarketplace, { loading: addingMarketplace }] = useMutation(ADD_MARKETPLACE);
  // const [deleteMarketplace] = useMutation(DELETE_MARKETPLACE);
  const [newMarketplaceName, setNewMarketplaceName] = useState<string>("");
  const [newMarketplaceFee, setNewMarketplaceFee] = useState<number>(8);

  // Email Campaign States
  const [selectedTemplate, setSelectedTemplate] = useState<"default" | "announcement" | "promotion" | "newsletter">("default");
  const [emailSubject, setEmailSubject] = useState<string>("");
  const [emailBody, setEmailBody] = useState<string>("");
  const [emailRecipient, setEmailRecipient] = useState<"all" | "select" | "filter">("all");
  const [userSearchText, setUserSearchText] = useState<string>("");
  const [selectedUsers, setSelectedUsers] = useState<CampaignUser[]>([]);
  const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [emailSendMode, setEmailSendMode] = useState<EmailSendMode>("now");
  const [scheduledAt, setScheduledAt] = useState<string>("");
  const [campaignHistory, setCampaignHistory] = useState<EmailCampaignHistoryItem[]>([]);
  const [scheduledCampaigns, setScheduledCampaigns] = useState<ScheduledEmailCampaignItem[]>([]);
  const [historyTab, setHistoryTab] = useState<"scheduled" | "sent">("scheduled");
  const [editingCampaign, setEditingCampaign] = useState<ScheduledEmailCampaignItem | null>(null);
  const [editSubject, setEditSubject] = useState<string>("");
  const [editBody, setEditBody] = useState<string>("");
  const [editTemplate, setEditTemplate] = useState<"default" | "announcement" | "promotion" | "newsletter">("default");
  const [editScheduledAt, setEditScheduledAt] = useState<string>("");
  const [headerImagePreview, setHeaderImagePreview] = useState<string>("");
  const [footerImagePreview, setFooterImagePreview] = useState<string>("");
  const [editHeaderImagePreview, setEditHeaderImagePreview] = useState<string>("");
  const [editFooterImagePreview, setEditFooterImagePreview] = useState<string>("");
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);

  // Push Notification Campaign States
  const [pushTitle, setPushTitle] = useState<string>("");
  const [pushBody, setPushBody] = useState<string>("");
  const [pushRecipient, setPushRecipient] = useState<"all" | "select" | "filter">("all");
  const [recipientFilters, setRecipientFilters] = useState<CampaignRecipientFilterState>(
    DEFAULT_CAMPAIGN_RECIPIENT_FILTERS,
  );
  const [filterResolvedUsers, setFilterResolvedUsers] = useState<CampaignUser[]>([]);
  const [isResolvingFilterUsers, setIsResolvingFilterUsers] = useState(false);
  const [filterResolveError, setFilterResolveError] = useState<string | null>(null);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [pushUserSearchText, setPushUserSearchText] = useState<string>("");
  const [selectedPushUsers, setSelectedPushUsers] = useState<CampaignUser[]>([]);
  const [pushFrequency, setPushFrequency] = useState<PushFrequency>("one_time");
  const [pushScheduledAt, setPushScheduledAt] = useState<string>("");
  const [pushCampaigns, setPushCampaigns] = useState<PushNotificationCampaignItem[]>([]);
  const [pushStatusFilter, setPushStatusFilter] = useState<"all" | PushCampaignStatus>("all");
  const [pushLoading, setPushLoading] = useState<boolean>(false);
  const [isSavingPush, setIsSavingPush] = useState<boolean>(false);
  const [editingPushCampaign, setEditingPushCampaign] = useState<PushNotificationCampaignItem | null>(null);
  const [previewPushCampaign, setPreviewPushCampaign] = useState<PushNotificationCampaignItem | null>(null);
  const [editPushTitle, setEditPushTitle] = useState<string>("");
  const [editPushBody, setEditPushBody] = useState<string>("");
  const [editPushFrequency, setEditPushFrequency] = useState<PushFrequency>("one_time");
  const [editPushScheduledAt, setEditPushScheduledAt] = useState<string>("");
  const [editPushStatus, setEditPushStatus] = useState<"draft" | "active" | "paused">("draft");
  
  // Refs for file inputs to clear them after successful send
  const headerImageInputRef = useRef<HTMLInputElement>(null);
  const footerImageInputRef = useRef<HTMLInputElement>(null);
  const editHeaderImageInputRef = useRef<HTMLInputElement>(null);
  const editFooterImageInputRef = useRef<HTMLInputElement>(null);
  const [selectedSection, setSelectedSection] = useState("vlFee");
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });
  const [previewSentItem, setPreviewSentItem] = useState<EmailCampaignHistoryItem | null>(null);
  const [previewScheduledItem, setPreviewScheduledItem] = useState<ScheduledEmailCampaignItem | null>(null);
  
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    marketplaceId: string | null;
    marketplaceName: string;
  }>({
    isOpen: false,
    marketplaceId: null,
    marketplaceName: "",
  });

  const handleImageFileChange = async (
    file: File | null,
    setPreview: React.Dispatch<React.SetStateAction<string>>,
  ) => {
    if (!file) {
      setPreview("");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      error("Image file size must be under 2MB.");
      setPreview("");
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setPreview(dataUrl);
    } catch (err) {
      console.error("Image upload failed:", err);
      error("Failed to read the selected image.");
    }
  };

  const handleHeaderImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    await handleImageFileChange(file, setHeaderImagePreview);
  };

  const handleFooterImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    await handleImageFileChange(file, setFooterImagePreview);
  };

  const handleEditHeaderImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    await handleImageFileChange(file, setEditHeaderImagePreview);
  };

  const handleEditFooterImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    await handleImageFileChange(file, setEditFooterImagePreview);
  };

  const [searchQuery, setSearchQuery] = useState("");
  
  // Marketplace search state
  const [marketplaceSearchQuery, setMarketplaceSearchQuery] = useState<string>("");
  const { data: campaignUsersData, loading: campaignUsersLoading } = useQuery(GET_EMAIL_CAMPAIGN_USERS, {
    variables: {
      searchText: `%${userSearchText.trim() || ""}%`,
      limit: 20,
      offset: 0,
    },
    skip: selectedSection !== "emailCampaigns",
    fetchPolicy: "network-only",
  });

  const { data: pushUsersData, loading: pushUsersLoading } = useQuery(GET_PUSH_NOTIFICATION_USERS, {
    variables: {
      searchText: `%${pushUserSearchText.trim() || ""}%`,
      limit: 50,
      offset: 0,
    },
    skip: selectedSection !== "pushNotifications",
    fetchPolicy: "network-only",
  });

  const { data: allUsersDataForAffiliateAdmin, loading: allUsersLoadingForAffiliateAdmin } = useQuery(GET_USERS_LIST, {
    skip: (!isAffiliateAdmin && !isMarketingUser) || (selectedSection !== "emailCampaigns" && selectedSection !== "pushNotifications"),
    variables: {
      pageSize: 5000,
      pageOffset: 0,
      searchText: `%${(selectedSection === "pushNotifications" ? pushUserSearchText : userSearchText).trim() || ""}%`,
      order_by: [{ u_email_id: "asc" }],
    },
    fetchPolicy: "network-only",
  });
  
  const openPreviewSent = (item: EmailCampaignHistoryItem) => {
    setPreviewSentItem(item);
  };

  const closePreview = () => {
    setPreviewSentItem(null);
    setPreviewScheduledItem(null);
  };

  const openPreviewScheduled = (item: ScheduledEmailCampaignItem) => {
    setPreviewScheduledItem(item);
  };
  
  const { data: affiliatesData } = useQuery(GET_ALL_AFFILIATES, {
    skip: !isAffiliateAdmin || (selectedSection !== "emailCampaigns" && selectedSection !== "pushNotifications"),
    variables: {
      pageSize: 1000,
      pageNumber: 0,
      order_by: {},
      search_event: "%"
    },
    fetchPolicy: "network-only",
  });

  const { data: salesRepByEmailData } = useQuery(GET_SALES_REPS_BY_EMAIL, {
    skip: !isMarketingUser || !loggedInEmail || (selectedSection !== "emailCampaigns" && selectedSection !== "pushNotifications"),
    variables: { email: loggedInEmail },
    fetchPolicy: "network-only",
  });

  const { data: availableTeamsData, loading: campaignTeamsLoading } = useQuery(GET_AVAILABLE_TEAMS_FOR_USER, {
    skip: selectedSection !== "emailCampaigns" && selectedSection !== "pushNotifications",
    fetchPolicy: "cache-first",
  });

  const apolloClient = useApolloClient();

  const templateColors: { [key: string]: string } = {
    default: "#5D75F8",
    announcement: "#10B981",
    promotion: "#F59E0B",
    newsletter: "#8B5CF6",
  };
  
  const { success, error } = ToasterWithMessage();

  const getNodeAuthHeaders = () => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes("idToken")) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : "";
    const hasuraRole = localStorage.getItem("x-hasura-user-role") || "";
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (idToken) {
      headers.Authorization = `Bearer ${idToken}`;
    }
    if (hasuraRole) {
      headers["x-hasura-role"] = hasuraRole;
    }
    return headers;
  };

  const loadCampaignHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await fetch(buildNodeApiUrl("/api/email-campaign-history"), {
        method: "POST",
        headers: getNodeAuthHeaders(),
        body: JSON.stringify({
          input: {
            limit: 10,
            offset: 0,
          },
        }),
      });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responseData?.message || `Request failed with status ${response.status}`);
      }
      setCampaignHistory(Array.isArray(responseData?.campaigns) ? responseData.campaigns : []);
    } catch (err) {
      console.error("email-campaign-history failed:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadScheduledCampaigns = async () => {
    try {
      setHistoryLoading(true);
      const response = await fetch(buildNodeApiUrl("/api/email-campaign-schedules"), {
        method: "POST",
        headers: getNodeAuthHeaders(),
        body: JSON.stringify({
          input: {
            limit: 20,
            offset: 0,
          },
        }),
      });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responseData?.message || `Request failed with status ${response.status}`);
      }
      setScheduledCampaigns(Array.isArray(responseData?.campaigns) ? responseData.campaigns : []);
    } catch (err) {
      console.error("email-campaign-schedules failed:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // ==================== DELETE MARKETPLACE FUNCTIONS ====================
  // const handleDeleteMarketplace = async () => {
  //   if (!deleteConfirm.marketplaceId) return;
  //   if (vlFeeReadOnly) {
  //     error("You don't have permission to delete marketplaces.");
  //     setDeleteConfirm({ isOpen: false, marketplaceId: null, marketplaceName: "" });
  //     return;
  //   }

  //   try {
  //     await deleteMarketplace({
  //       variables: { id: deleteConfirm.marketplaceId }
  //     });
  //     await refetchMarketplaces();
  //     success(`Marketplace "${deleteConfirm.marketplaceName}" deleted successfully!`);
  //     setDeleteConfirm({ isOpen: false, marketplaceId: null, marketplaceName: "" });
  //   } catch (e: any) {
  //     const errorMessage = e?.message || String(e);
  //     if (errorMessage.toLowerCase().includes("foreign key") || 
  //         errorMessage.toLowerCase().includes("constraint")) {
  //       error("Cannot delete this marketplace as it is being used by existing data.");
  //     } else {
  //       error("Failed to delete marketplace. Please try again.");
  //     }
  //     console.error('Delete marketplace failed:', e);
  //   }
  // };

  const openDeleteConfirm = (id: string, name: string) => {
    if (id.startsWith("static:")) {
      error("This is a default marketplace and cannot be deleted.");
      return;
    }
    setDeleteConfirm({
      isOpen: true,
      marketplaceId: id,
      marketplaceName: name,
    });
  };

  const openEditScheduledCampaign = (campaign: ScheduledEmailCampaignItem) => {
    setEditingCampaign(campaign);
    setEditSubject(campaign.ecs_subject || "");
    setEditBody(campaign.ecs_body || "");
    setEditHeaderImagePreview(campaign.ecs_header_image || "");
    setEditFooterImagePreview(campaign.ecs_footer_image || "");
    const template = (campaign.ecs_template_type || "default").toLowerCase();
    setEditTemplate((["default", "announcement", "promotion", "newsletter"].includes(template) ? template : "default") as any);
    setEditScheduledAt(toDateTimeLocalInputValue(campaign.ecs_scheduled_at));
  };

  const handleUpdateScheduledCampaign = async () => {
    if (!editingCampaign) return;
    if (!editSubject.trim()) {
      error("Email subject is required.");
      return;
    }
    if (!editBody.trim()) {
      error("Email body is required.");
      return;
    }
    if (!editScheduledAt) {
      error("Scheduled time is required.");
      return;
    }
    const scheduleDate = new Date(editScheduledAt);
    if (Number.isNaN(scheduleDate.getTime()) || scheduleDate.getTime() <= Date.now()) {
      error("Scheduled time must be in the future.");
      return;
    }

    try {
      const response = await fetch(buildNodeApiUrl("/api/update-scheduled-email-campaign"), {
        method: "POST",
        headers: getNodeAuthHeaders(),
        body: JSON.stringify({
          input: {
            ecs_id: editingCampaign.ecs_id,
            subject: editSubject.trim(),
            body: editBody.trim(),
            templateType: editTemplate,
            headerImage: editHeaderImagePreview || null,
            footerImage: editFooterImagePreview || null,
            scheduledAt: scheduleDate.toISOString(),
          },
        }),
      });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responseData?.message || `Request failed with status ${response.status}`);
      }
      success("Scheduled email updated.");
      setEditingCampaign(null);
      loadScheduledCampaigns();
    } catch (err: any) {
      error(err?.message || "Failed to update scheduled email.");
    }
  };

  const handleDeleteScheduledCampaign = async (campaignId: string) => {
    try {
      const response = await fetch(buildNodeApiUrl("/api/delete-scheduled-email-campaign"), {
        method: "POST",
        headers: getNodeAuthHeaders(),
        body: JSON.stringify({ input: { ecs_id: campaignId } }),
      });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responseData?.message || `Request failed with status ${response.status}`);
      }
      success("Scheduled email deleted.");
      loadScheduledCampaigns();
    } catch (err: any) {
      error(err?.message || "Failed to delete scheduled email.");
    }
  };

  useEffect(() => {
    setFiltersApplied(false);
    setFilterResolvedUsers([]);
    setFilterResolveError(null);
  }, [recipientFilters]);

  useEffect(() => {
    if (selectedSection === "emailCampaigns") {
      loadCampaignHistory();
      loadScheduledCampaigns();
    }
    if (selectedSection === "pushNotifications") {
      loadPushCampaigns();
    }
  }, [selectedSection]);

  const selectedEmailSet = React.useMemo(
    () => new Set(selectedUsers.map((user) => user.u_email_id.toLowerCase())),
    [selectedUsers],
  );

  const isUserEmailEnabled = (user: CampaignUser) => user.u_email_enabled !== false;
  const getUserEmailStatusLabel = (user: CampaignUser) =>
    user.u_email_enabled === false ? "Disabled via User Details" : "Enabled";

  const scopedAffiliateId = React.useMemo(() => {
    if (!isAffiliateAdmin || !loggedInEmail) return null;
    const list = affiliatesData?.affiliates || [];
    const match = list.find((item: any) => (item?.a_email_id || "").toLowerCase().trim() === loggedInEmail);
    return match?.a_id || null;
  }, [affiliatesData, isAffiliateAdmin, loggedInEmail]);

  const scopedSalesRepId = React.useMemo(
    () => salesRepByEmailData?.sales_reps?.[0]?.sr_id || globalState?.profile?.id || null,
    [salesRepByEmailData, globalState?.profile?.id],
  );

  const campaignTeamsList = React.useMemo(
    () =>
      (availableTeamsData?.league_categories || [])
        .filter((team: any) => team?.lc_id && team?.lc_name)
        .map((team: any) => ({
          value: team.lc_id as string,
          label: team.lc_abbrev ? `${team.lc_name} (${team.lc_abbrev})` : team.lc_name,
        }))
        .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label)),
    [availableTeamsData],
  );

  const clearRecipientFilters = React.useCallback(() => {
    setRecipientFilters(DEFAULT_CAMPAIGN_RECIPIENT_FILTERS);
    setFilterResolvedUsers([]);
    setFilterResolveError(null);
    setFiltersApplied(false);
  }, []);

  const resolveCampaignFilterUsers = React.useCallback(async () => {
    setFilterResolveError(null);

    if (!hasActiveCampaignRecipientFilters(recipientFilters)) {
      setFilterResolveError("Select at least one filter: My Teams and/or ticket status.");
      return;
    }

    if (
      recipientFilters.ticketTab &&
      recipientFilters.datePreset === "custom" &&
      (!recipientFilters.customStartDate || !recipientFilters.customEndDate)
    ) {
      setFilterResolveError("Select both start and end dates for the custom range.");
      return;
    }

    setIsResolvingFilterUsers(true);
    try {
      let users: CampaignUser[] = [];

      if (recipientFilters.ticketTab) {
        const ticketQuery = getTicketFilterQuery(recipientFilters.ticketTab);
        if (!ticketQuery) {
          throw new Error("Unsupported ticket status filter.");
        }

        const { data: ticketData } = await apolloClient.query({
          query: ticketQuery,
          variables: buildTicketFilterQueryVariables(recipientFilters),
          fetchPolicy: "network-only",
        });
        const ticketRows = getTicketRowsFromQueryResult(recipientFilters.ticketTab, ticketData);
        const userIds = ticketRowsToUserIds(ticketRows);

        if (userIds.length > 0) {
          const { data: userData } = await apolloClient.query({
            query: GET_CAMPAIGN_USERS_BY_IDS,
            variables: { userIds },
            fetchPolicy: "network-only",
          });
          users = userData?.user || [];
        }
      } else {
        const { data: userData } = await apolloClient.query({
          query: GET_CAMPAIGN_FILTER_USERS,
          fetchPolicy: "network-only",
        });
        users = userData?.user || [];
      }

      if (recipientFilters.selectedTeam) {
        if (users.length > 0) {
          const { data: preferenceData } = await apolloClient.query({
            query: GET_USERS_PREFERENCE_TEAMS,
            variables: { userIds: users.map((user) => user.u_id) },
            fetchPolicy: "network-only",
          });
          const preferencesByUserId = new Map<string, CampaignUser["user_preference"]>();
          (preferenceData?.user_preference || []).forEach((pref: any) => {
            const existing = preferencesByUserId.get(pref.u_id) || [];
            preferencesByUserId.set(pref.u_id, [...existing, pref]);
          });
          users = users.map((user) => ({
            ...user,
            user_preference: preferencesByUserId.get(user.u_id) || [],
          }));
        }

        users = users.filter((user) =>
          userMatchesTeamFilter(user, recipientFilters.selectedTeam, campaignTeamsList),
        );
      }

      users = applyCampaignUserScope(users, {
        isAffiliateAdmin,
        isMarketingUser,
        scopedAffiliateId,
        loggedInEmail,
        scopedSalesRepId,
      }) as CampaignUser[];

      setFilterResolvedUsers(users);
      setFiltersApplied(true);
    } catch (err: any) {
      console.error("resolveCampaignFilterUsers failed:", err);
      setFilterResolveError(err?.message || "Failed to resolve matching users.");
      setFilterResolvedUsers([]);
      setFiltersApplied(false);
    } finally {
      setIsResolvingFilterUsers(false);
    }
  }, [
    apolloClient,
    campaignTeamsList,
    isAffiliateAdmin,
    isMarketingUser,
    loggedInEmail,
    recipientFilters,
    scopedAffiliateId,
    scopedSalesRepId,
  ]);

  const filterEligibleEmailUsers = React.useMemo(
    () => filterResolvedUsers.filter((user) => user.u_email_enabled !== false),
    [filterResolvedUsers],
  );
  const filterEligiblePushUsers = React.useMemo(
    () =>
      filterResolvedUsers.filter(
        (user) =>
          user.u_notification_enabled !== false &&
          Array.isArray(user.u_device_tokens) &&
          user.u_device_tokens.length > 0,
      ),
    [filterResolvedUsers],
  );

  const scopedCampaignUsers = React.useMemo(() => {
    const users = selectedSection === "pushNotifications"
      ? (pushUsersData?.user || [])
      : (isAffiliateAdmin || isMarketingUser)
      ? (allUsersDataForAffiliateAdmin?.user || [])
      : (campaignUsersData?.user || []);
    if (isMarketingUser) {
      const salesRepId = salesRepByEmailData?.sales_reps?.[0]?.sr_id || globalState?.profile?.id || null;
      if (!salesRepId) return [];
      return users.filter((user: any) => user?.u_sales_reps_id === salesRepId);
    }
    if (!isAffiliateAdmin) return users;
    return users.filter((user: any) => {
      const userAffiliateId = user?.u_affiliate_id || user?.affiliate?.a_id || null;
      const userAffiliateEmail = (user?.affiliate?.a_email_id || "").toLowerCase().trim();
      if (scopedAffiliateId && userAffiliateId === scopedAffiliateId) {
        return true;
      }
      return Boolean(loggedInEmail) && userAffiliateEmail === loggedInEmail;
    });
  }, [campaignUsersData, pushUsersData, allUsersDataForAffiliateAdmin, selectedSection, isAffiliateAdmin, isMarketingUser, scopedAffiliateId, loggedInEmail, salesRepByEmailData, globalState?.profile?.id]);

  const activeScopedCampaignUsers: CampaignUser[] = React.useMemo(
    () => scopedCampaignUsers.filter((user: any) => user.u_is_active !== false && !user.u_delete_requested),
    [scopedCampaignUsers],
  );

  const eligibleEmailScopedUsersCount = React.useMemo(
    () => activeScopedCampaignUsers.filter((user: CampaignUser) => user.u_email_enabled !== false).length,
    [activeScopedCampaignUsers],
  );

  const campaignUsers: CampaignUser[] = activeScopedCampaignUsers.filter(
    (user: CampaignUser) => !selectedEmailSet.has((user.u_email_id || "").toLowerCase()),
  );
  const activeUsersCount = isAffiliateAdmin
    ? eligibleEmailScopedUsersCount
    : isMarketingUser
    ? eligibleEmailScopedUsersCount
    : Number(campaignUsersData?.user_aggregate?.aggregate?.count || 0);
  const isCampaignUsersLoading = selectedSection === "pushNotifications"
    ? pushUsersLoading
    : (isAffiliateAdmin || isMarketingUser) ? allUsersLoadingForAffiliateAdmin : campaignUsersLoading;
  const pushEligibleScopedUsers = activeScopedCampaignUsers.filter(
    (user: CampaignUser) => user.u_notification_enabled !== false,
  );
  const selectedPushUserIdSet = React.useMemo(
    () => new Set(selectedPushUsers.map((user) => user.u_id)),
    [selectedPushUsers],
  );
  const pushCampaignUsers: CampaignUser[] = pushEligibleScopedUsers.filter(
    (user: CampaignUser) => !selectedPushUserIdSet.has(user.u_id),
  );
  const activePushUsersCount = (isAffiliateAdmin || isMarketingUser)
    ? pushEligibleScopedUsers.length
    : Number(pushUsersData?.push_user_aggregate?.aggregate?.count || 0);
  const filteredCampaignHistory = campaignHistory.filter(
    (item) => (item.ech_template_type || "default").toLowerCase() === selectedTemplate,
  );
  const filteredScheduledCampaigns = scheduledCampaigns.filter(
    (item) => (item.ecs_template_type || "default").toLowerCase() === selectedTemplate,
  );
  const filteredPushCampaigns = pushCampaigns.filter((campaign) => {
    if (pushStatusFilter === "all") return true;
    return campaign.pnc_status === pushStatusFilter;
  });
  const formatHistoryTemplateName = (templateType?: string) => {
    const value = (templateType || "default").trim();
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : "Default";
  };

  const emailEligibleScopedUsers: CampaignUser[] = React.useMemo(
    () => activeScopedCampaignUsers.filter((user: CampaignUser) => user.u_email_enabled !== false),
    [activeScopedCampaignUsers],
  );
  const emailEligibleScopedEmails = React.useMemo<string[]>(
    () => emailEligibleScopedUsers.map((user: CampaignUser) => user.u_email_id),
    [emailEligibleScopedUsers],
  );
  const disabledEmailScopedCount = React.useMemo(
    () => activeScopedCampaignUsers.filter((user: CampaignUser) => user.u_email_enabled === false).length,
    [activeScopedCampaignUsers],
  );
  const formatHistoryRecipientsLabel = (recipientType?: string) => {
    const normalized = (recipientType || "").trim().toLowerCase();
    if (normalized === "all") return "All Active Users";
    if (normalized === "select") return "Selected Users";
    return "Users";
  };
  const previewHtml = buildCampaignEmailHtml(emailBody, headerImagePreview || undefined, footerImagePreview || undefined);

  const handleSelectUser = (user: CampaignUser) => {
    if (!user?.u_email_id) return;
    if (user.u_email_enabled === false) {
      error("This user's email is disabled in User Details and cannot be selected for campaigns.");
      return;
    }
    if (selectedEmailSet.has(user.u_email_id.toLowerCase())) return;
    setSelectedUsers((prev) => [...prev, user]);
    setUserSearchText("");
  };

  const handleRemoveSelectedUser = (email: string) => {
    setSelectedUsers((prev) => prev.filter((user) => user.u_email_id !== email));
  };

  const resetPushForm = () => {
    setPushTitle("");
    setPushBody("");
    setPushRecipient("all");
    setPushUserSearchText("");
    setSelectedPushUsers([]);
    setPushFrequency("one_time");
    setPushScheduledAt("");
    clearRecipientFilters();
  };

  const loadPushCampaigns = async () => {
    try {
      setPushLoading(true);
      const response = await fetch(buildNodeApiUrl("/api/push-notification-campaigns"), {
        method: "POST",
        headers: getNodeAuthHeaders(),
        body: JSON.stringify({
          input: {
            limit: 50,
            offset: 0,
          },
        }),
      });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responseData?.message || `Request failed with status ${response.status}`);
      }
      setPushCampaigns(Array.isArray(responseData?.campaigns) ? responseData.campaigns : []);
    } catch (err) {
      console.error("push-notification-campaigns failed:", err);
    } finally {
      setPushLoading(false);
    }
  };

  const handleSelectPushUser = (user: CampaignUser) => {
    if (!user?.u_id || selectedPushUserIdSet.has(user.u_id)) return;
    setSelectedPushUsers((prev) => [...prev, user]);
    setPushUserSearchText("");
  };

  const handleRemoveSelectedPushUser = (userId: string) => {
    setSelectedPushUsers((prev) => prev.filter((user) => user.u_id !== userId));
  };

  const buildPushPayload = (status: "draft" | "active") => {
    const allScopedUsers = pushEligibleScopedUsers.map((user: CampaignUser) => ({
      id: user.u_id,
      email: user.u_email_id,
    }));
    const selectedRecipients: Array<{ id: string; email: string }> =
      pushRecipient === "filter"
        ? filterEligiblePushUsers.map((user) => ({ id: user.u_id, email: user.u_email_id }))
        : pushRecipient === "select"
        ? selectedPushUsers.map((user) => ({ id: user.u_id, email: user.u_email_id }))
        : (isAffiliateAdmin || isMarketingUser)
          ? allScopedUsers
          : [];
    const recipientTypeToSend =
      pushRecipient === "all" && (isAffiliateAdmin || isMarketingUser)
        ? "select"
        : pushRecipient === "filter"
          ? "select"
          : pushRecipient;

    return {
      title: pushTitle.trim(),
      body: pushBody.trim(),
      recipientType: recipientTypeToSend,
      selectedUserIds: selectedRecipients.map((user) => user.id),
      selectedEmails: selectedRecipients.map((user) => user.email),
      frequency: pushFrequency,
      status,
      scheduledAt: pushScheduledAt ? new Date(pushScheduledAt).toISOString() : null,
    };
  };

  const validatePushForm = (status: "draft" | "active") => {
    if (!pushTitle.trim()) {
      error("Push notification title is required.");
      return false;
    }
    if (!pushBody.trim()) {
      error("Push notification body is required.");
      return false;
    }
    if (pushRecipient === "select" && selectedPushUsers.length === 0) {
      error("Select at least one push notification recipient.");
      return false;
    }
    if (pushRecipient === "filter") {
      if (!filtersApplied || filterEligiblePushUsers.length === 0) {
        error("Apply filters and ensure at least one push-eligible user is found.");
        return false;
      }
    }
    if (status === "active" && pushScheduledAt) {
      const scheduledDate = new Date(pushScheduledAt);
      if (Number.isNaN(scheduledDate.getTime())) {
        error("Invalid scheduled date and time.");
        return false;
      }
    }
    return true;
  };

  const handleSavePushCampaign = async (status: "draft" | "active") => {
    if (!validatePushForm(status)) return;

    try {
      setIsSavingPush(true);
      const response = await fetch(buildNodeApiUrl("/api/create-push-notification-campaign"), {
        method: "POST",
        headers: getNodeAuthHeaders(),
        body: JSON.stringify({ input: buildPushPayload(status) }),
      });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responseData?.message || `Request failed with status ${response.status}`);
      }

      if (status === "draft") {
        success("Push notification draft saved.");
      } else if (responseData?.delivery) {
        success(`Push notification sent to ${responseData.delivery.sentSuccessfully} device(s).`);
      } else if (pushScheduledAt) {
        success(`Push notification scheduled for ${new Date(pushScheduledAt).toLocaleString()}.`);
      } else {
        success("Push notification campaign activated.");
      }
      resetPushForm();
      loadPushCampaigns();
    } catch (err: any) {
      error(err?.message || "Failed to save push notification campaign.");
      console.error("create-push-notification-campaign failed:", err);
    } finally {
      setIsSavingPush(false);
    }
  };

  const openEditPushCampaign = (campaign: PushNotificationCampaignItem) => {
    setEditingPushCampaign(campaign);
    setEditPushTitle(campaign.pnc_title || "");
    setEditPushBody(campaign.pnc_body || "");
    setEditPushFrequency(campaign.pnc_frequency || "one_time");
    setEditPushScheduledAt(toDateTimeLocalInputValue(campaign.pnc_scheduled_at));
    setEditPushStatus(["draft", "active", "paused"].includes(campaign.pnc_status) ? campaign.pnc_status as any : "draft");
  };

  const handleUpdatePushCampaign = async () => {
    if (!editingPushCampaign) return;
    if (!editPushTitle.trim()) {
      error("Push notification title is required.");
      return;
    }
    if (!editPushBody.trim()) {
      error("Push notification body is required.");
      return;
    }

    try {
      const response = await fetch(buildNodeApiUrl("/api/update-push-notification-campaign"), {
        method: "POST",
        headers: getNodeAuthHeaders(),
        body: JSON.stringify({
          input: {
            pnc_id: editingPushCampaign.pnc_id,
            title: editPushTitle.trim(),
            body: editPushBody.trim(),
            recipientType: editingPushCampaign.pnc_recipient_type,
            selectedUserIds: editingPushCampaign.pnc_selected_user_ids || [],
            selectedEmails: editingPushCampaign.pnc_selected_emails || [],
            frequency: editPushFrequency,
            status: editPushStatus,
            scheduledAt: editPushScheduledAt ? new Date(editPushScheduledAt).toISOString() : null,
          },
        }),
      });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responseData?.message || `Request failed with status ${response.status}`);
      }
      success("Push notification campaign updated.");
      setEditingPushCampaign(null);
      loadPushCampaigns();
    } catch (err: any) {
      error(err?.message || "Failed to update push notification campaign.");
    }
  };

  const handleUpdatePushCampaignStatus = async (campaign: PushNotificationCampaignItem, status: "active" | "paused") => {
    try {
      const response = await fetch(buildNodeApiUrl("/api/update-push-notification-campaign-status"), {
        method: "POST",
        headers: getNodeAuthHeaders(),
        body: JSON.stringify({
          input: {
            pnc_id: campaign.pnc_id,
            status,
            nextRunAt: status === "active" ? (campaign.pnc_next_run_at || campaign.pnc_scheduled_at || null) : null,
          },
        }),
      });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responseData?.message || `Request failed with status ${response.status}`);
      }
      success(status === "paused" ? "Push notification paused." : "Push notification resumed.");
      loadPushCampaigns();
    } catch (err: any) {
      error(err?.message || "Failed to update push notification status.");
    }
  };

  const handleDeletePushCampaign = async (campaignId: string) => {
    try {
      const response = await fetch(buildNodeApiUrl("/api/delete-push-notification-campaign"), {
        method: "POST",
        headers: getNodeAuthHeaders(),
        body: JSON.stringify({ input: { pnc_id: campaignId } }),
      });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responseData?.message || `Request failed with status ${response.status}`);
      }
      success("Push notification campaign deleted.");
      loadPushCampaigns();
    } catch (err: any) {
      error(err?.message || "Failed to delete push notification campaign.");
    }
  };

  const handlePreviewEmail = () => {
    if (!emailSubject.trim()) {
      error("Email subject is required for preview.");
      return;
    }
    if (!emailBody.trim()) {
      error("Email body is required for preview.");
      return;
    }
    if (emailRecipient === "select" && selectedUsers.length === 0) {
      error("Select at least one user to preview targeted email.");
      return;
    }
    if (emailRecipient === "filter" && filterEligibleEmailUsers.length === 0) {
      error("Apply filters and ensure at least one email-eligible user is found.");
      return;
    }
    setIsPreviewOpen(true);
  };

  const handleSendEmailCampaign = async () => {
    if (!emailSubject.trim()) {
      error("Email subject is required.");
      return;
    }
    if (!emailBody.trim()) {
      error("Email body is required.");
      return;
    }
    if (emailSendMode === "schedule") {
      if (!scheduledAt) {
        error("Please choose a schedule date and time.");
        return;
      }
      const scheduledDate = new Date(scheduledAt);
      if (Number.isNaN(scheduledDate.getTime())) {
        error("Invalid schedule date and time.");
        return;
      }
      if (scheduledDate.getTime() <= Date.now()) {
        error("Scheduled time must be in the future.");
        return;
      }
    }

    if (emailRecipient === "filter") {
      if (!filtersApplied || filterEligibleEmailUsers.length === 0) {
        error("Apply filters and ensure at least one email-eligible user is found.");
        return;
      }
    }

    const allScopedEmails = emailEligibleScopedEmails;
    const selectedEmails =
      emailRecipient === "filter"
        ? filterEligibleEmailUsers.map((user) => user.u_email_id)
        : emailRecipient === "select"
        ? selectedUsers.filter(isUserEmailEnabled).map((user) => user.u_email_id)
        : (isAffiliateAdmin || isMarketingUser)
          ? allScopedEmails
          : [];
    const recipientTypeToSend =
      emailRecipient === "all" && (isAffiliateAdmin || isMarketingUser)
        ? "select"
        : emailRecipient === "filter"
          ? "select"
          : emailRecipient;

    if (recipientTypeToSend === "select" && selectedEmails.length === 0) {
      error("Select at least one user.");
      return;
    }
    if (emailRecipient === "all" && (isAffiliateAdmin || isMarketingUser) && allScopedEmails.length === 0) {
      error("No users are eligible to receive campaigns because their email is disabled.");
      return;
    }

    try {
      setIsSendingEmail(true);
      const response = await fetch(buildNodeApiUrl("/api/send-email-campaign"), {
        method: "POST",
        headers: getNodeAuthHeaders(),
        body: JSON.stringify({
          input: {
            subject: emailSubject.trim(),
            body: emailBody.trim(),
            templateType: selectedTemplate,
            recipientType: recipientTypeToSend,
            selectedEmails,
            headerImage: headerImagePreview || null,
            footerImage: footerImagePreview || null,
            scheduleAt: emailSendMode === "schedule" ? new Date(scheduledAt).toISOString() : null,
          },
        }),
      });

      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responseData?.message || `Request failed with status ${response.status}`);
      }

      const sentSuccessfully = Number(responseData?.sentSuccessfully || 0);
      const failedCount = Number(responseData?.failed || 0);
      if (responseData?.scheduled) {
        success(`Email scheduled for ${new Date(responseData?.scheduledFor).toLocaleString()}.`);
        setSelectedTemplate("default");
        setEmailSubject("");
        setEmailBody("");
        setEmailRecipient("all");
        setUserSearchText("");
        setSelectedUsers([]);
        clearRecipientFilters();
        setEmailSendMode("now");
        setScheduledAt("");
        setHeaderImagePreview("");
        setFooterImagePreview("");
        if (headerImageInputRef.current) headerImageInputRef.current.value = "";
        if (footerImageInputRef.current) footerImageInputRef.current.value = "";
        setIsPreviewOpen(false);
        loadScheduledCampaigns();
        return;
      }

      if (sentSuccessfully === 0) {
        error(responseData?.message || "Email delivery failed for all recipients.");
        return;
      }

      if (failedCount > 0) {
        error(`Email sent to ${sentSuccessfully}, failed for ${failedCount}.`);
      } else {
        success(`Email sent successfully to ${sentSuccessfully} user(s).`);
        // Reset compose form only when the campaign is fully successful.
        setSelectedTemplate("default");
        setEmailSubject("");
        setEmailBody("");
        setEmailRecipient("all");
        setUserSearchText("");
        setSelectedUsers([]);
        clearRecipientFilters();
        setEmailSendMode("now");
        setScheduledAt("");
        setHeaderImagePreview("");
        setFooterImagePreview("");
        if (headerImageInputRef.current) headerImageInputRef.current.value = "";
        if (footerImageInputRef.current) footerImageInputRef.current.value = "";
        setIsPreviewOpen(false);
        loadCampaignHistory();
        loadScheduledCampaigns();
      }
    } catch (err: any) {
      const base = getNodeApiBaseUrl();
      const hint =
        err?.message === "Failed to fetch"
          ? ` Cannot reach Node API (${base || "proxied /api → http://localhost:4000"}). Start backend on port 4000 and restart npm start if you changed proxy.`
          : "";
      error((err?.message || "Failed to send email campaign.") + hint);
      console.error("send-email-campaign failed:", err);
    } finally {
      setIsSendingEmail(false);
    }
  };
  
  
  
  // Filtered and sorted marketplaces based on search query (stable order)
  const filteredMarketplaces = marketplaces
    .filter((marketplace) =>
      marketplace.name.toLowerCase().includes(marketplaceSearchQuery.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  // Handle Marketplace Toggle (single click, disables while updating)
  const handleMarketplaceToggle = async (id: string, currentActive: boolean) => {
    if (vlFeeReadOnly) {
      error("You don't have permission to change marketplace status.");
      return;
    }
    if (id.startsWith("static:")) return;
    setToggleLoading((prev) => ({ ...prev, [id]: true }));
    const newActive = !currentActive;
    try {
      await toggleMarketplace({ variables: { id, active: newActive } });
      await refetchMarketplaces();
    } catch (e) {
      error("Failed to update marketplace status.");
      console.error('Toggle update failed:', e);
    } finally {
      setToggleLoading((prev) => {
        const s = { ...prev };
        delete s[id];
        return s;
      });
    }
  };

  const handleRefresh = () => {
    refetchMarketplaces();
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  // Handle Add Marketplace
  const handleAddMarketplace = async () => {
    if (vlFeeReadOnly) {
      error("You don't have permission to add marketplaces.");
      return;
    }
    
    const trimmedName = newMarketplaceName.trim();
    if (!trimmedName) {
      error("Please enter a marketplace name.");
      return;
    }

    // Check for duplicate marketplace (case-insensitive)
    const existingMarketplace = marketplaces.find(
      (m) => m.name.toLowerCase() === trimmedName.toLowerCase()
    );
    
    if (existingMarketplace) {
      error(`Marketplace "${trimmedName}" already exists.`);
      return;
    }

    try {
      const fee = getNumericFee(newMarketplaceFee, 8);
      await addMarketplace({ 
        variables: { 
          name: trimmedName, 
          fee, 
          qaNets: computeQaNetLabel(fee) 
        } 
      });
      setNewMarketplaceName("");
      setNewMarketplaceFee(8);
      refetchMarketplaces();
      success(`Marketplace "${trimmedName}" added successfully!`);
    } catch (e: any) {
      // Check if the error is a duplicate key violation from the backend
      const errorMessage = e?.message || String(e);
      if (errorMessage.toLowerCase().includes("duplicate") || 
          errorMessage.toLowerCase().includes("unique constraint") ||
          errorMessage.toLowerCase().includes("already exists")) {
        error(`Marketplace "${trimmedName}" already exists.`);
      } else {
        error("Failed to add marketplace. Please try again.");
      }
      console.error('Add marketplace failed:', e);
    }
  };
  
  // Handle Fee Update (local state only — saved on blur or Save All)
  const handleFeeUpdate = (id: string, newFee: number) => {
    if (vlFeeReadOnly) return;
    if (!Number.isFinite(newFee)) return;
    setLocalFees(prev => ({ ...prev, [id]: newFee }));
  };

  const handleFeeSave = async (id: string) => {
    if (vlFeeReadOnly) {
      error("You don't have permission to edit marketplace percentages.");
      return;
    }
    if (id.startsWith("static:")) {
      error("This marketplace is from static defaults. Add it first to save changes.");
      return;
    }
    if (localFees[id] !== undefined) {
      try {
        const fee = getNumericFee(localFees[id], 0);
        await updateFee({ variables: { id, fee, qaNets: computeQaNetLabel(fee) } });
        setLocalFees(prev => { const s = { ...prev }; delete s[id]; return s; });
        refetchMarketplaces();
        // Do not show a global toast for per-input saves to avoid noisy UX.
        // The user can use "Save All" which will show a consolidated success message.
      } catch (e) {
        error("Failed to update marketplace fee.");
        console.error('Fee save failed:', e);
      }
    }
  };

  const handleSaveAll = async () => {
    if (vlFeeReadOnly) {
      error("You don't have permission to edit marketplace percentages.");
      return;
    }
    try {
      const entries = Object.entries(localFees).filter(([id]) => !id.startsWith("static:"));
      await Promise.all(
        entries.map(([id, fee]) => {
          const safeFee = getNumericFee(fee, 0);
          return updateFee({ variables: { id, fee: safeFee, qaNets: computeQaNetLabel(safeFee) } });
        }),
      );
      setLocalFees({});
      refetchMarketplaces();
      success("All marketplace fees saved successfully!");
    } catch (e) {
      error("Failed to save all marketplace fees.");
      console.error('Save all failed:', e);
    }
  };

  // Get marketplace icon color
  const getMarketplaceIconColor = (name: string): string => {
    const colors: { [key: string]: string } = {
      StubHub: "#FF6B35",
      SeatGeek: "#FF6B35",
      Ticketmaster: "#FF6B35",
      "Vivid Seats": "#646FD4",
      TickPick: "#FF6B35",
      Gametime: "#FFB100",
      TicketNetwork: "#FF6B35",
      GoTickets: "#1DB954",
      AXS: "#5D75F8",
      "Ticket Evolution": "#FF6B35",
    };
    return colors[name] || "#5D75F8";
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <p className={styles.subtitle}>Admin Settings</p>
          <h1 className={styles.title}>Settings</h1>
        </div>

        <div className={styles.headerActions}>
           {selectedSection === "vlFee" && (
          <div className={styles.searchWrapper}>
            <input
              type="text"
              placeholder="Search Marketplaces..."
              className={styles.searchInput}
              value={marketplaceSearchQuery}
                  onChange={(e) => setMarketplaceSearchQuery(e.target.value)}
            />
              {marketplaceSearchQuery && (
                  <button 
                    className={styles.clearSearchBtn}
                    onClick={() => setMarketplaceSearchQuery("")}
                  >
                    ✕
                  </button>
                )}
          </div>
           )}
          <button className={styles.iconBtn} onClick={handleRefresh}>
            <RefreshRoundedIcon />
          </button>
          <button className={styles.iconBtn} onClick={() => setIsDarkMode(!isDarkMode)}>
            {isDarkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
          </button>
        </div>
      </div>

      {/* Section Tabs */}
      <div className={styles.tabContainer}>
        <button
          className={`${styles.tab} ${selectedSection === "vlFee" ? styles.activeTab : ""}`}
          onClick={() => setSelectedSection("vlFee")}
        >
          VL Fee
        </button>
        <button
          className={`${styles.tab} ${selectedSection === "emailCampaigns" ? styles.activeTab : ""}`}
          onClick={() => setSelectedSection("emailCampaigns")}
        >
          Email Campaigns
        </button>
        <button
          className={`${styles.tab} ${selectedSection === "pushNotifications" ? styles.activeTab : ""}`}
          onClick={() => setSelectedSection("pushNotifications")}
        >
          Push Notifications
        </button>
      </div>

      {/* SECTION 1: VL Fee */}
      {selectedSection === "vlFee" && (
        <div className={styles.section}>
          <div className={styles.sectionHeaderRow}>
            <div className={styles.sectionHeader}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" x2="12" y1="2" y2="22"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
              <span>Marketplace VL Fees</span>
            </div>
            <div className={styles.sectionHeaderRight}>
              <button className={styles.saveAllButtonInline} onClick={handleSaveAll} disabled={vlFeeReadOnly}>Save All</button>
            </div>
          </div>
          <p className={styles.sectionDescription}>
            When inventory sells, the VL fee is charged based on which marketplace it sold on.
          </p>

          <div className={styles.infoBox}>
            <strong>Fee Logic:</strong>
            <br />
            Each marketplace has its own VL fee percentage
            <br />
            When a ticket sells, the fee is determined by the marketplace it sold on
            <br />
            QA Profit = (User Fee % - VL Fee %) x Sold Price
          </div>

          <div className={styles.tableWrapper}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow className={styles.tableHeaderRow}>
                    <TableCell className={styles.tableHeaderCell}>MARKETPLACE</TableCell>
                    <TableCell className={styles.tableHeaderCell} align="center">VL FEE %</TableCell>
                    <TableCell className={styles.tableHeaderCell} align="center">ACTIVE</TableCell>
                    <TableCell className={styles.tableHeaderCell} align="right">QA NETS (20% USER)</TableCell>
                    {/* <TableCell className={styles.tableHeaderCell} align="center">ACTIONS</TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {marketplacesLoading ? (
                    <TableRow><TableCell colSpan={5} align="center">Loading...</TableCell></TableRow>
                  ) : filteredMarketplaces.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        {marketplaceSearchQuery 
                          ? `No marketplaces found matching "${marketplaceSearchQuery}"` 
                          : "No marketplaces found. Add one below."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredMarketplaces.map((marketplace) => (
                      <TableRow key={marketplace.id} className={styles.tableRow}>
                        <TableCell className={styles.tableCell}>
                          <div className={styles.marketplaceCell}>
                            <div
                              className={styles.marketplaceIcon}
                              style={{ backgroundColor: getMarketplaceIconColor(marketplace.name) }}
                            >
                              {marketplace.name.charAt(0)}
                            </div>
                            {marketplace.name}
                          </div>
                        </TableCell>
                        <TableCell className={styles.tableCell} align="center">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={localFees[marketplace.id] ?? getNumericFee(marketplace.vl_fee_percentage, 0)}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (Number(value) >= 0) {
                                handleFeeUpdate(marketplace.id, parseFloat(value));
                              }
                            }}
                            onKeyDown={(e) => {
                              if (["-", "+", "e", "E"].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onBlur={() => handleFeeSave(marketplace.id)}
                            className={styles.feeInput}
                            disabled={vlFeeReadOnly || marketplace.id.startsWith("static:")}
                          />
                          <span className={styles.percentSymbol}>%</span>
                        </TableCell>
                        <TableCell className={styles.tableCell} align="center">
                          <Switch
                            checked={marketplace.active}
                            onChange={() => handleMarketplaceToggle(marketplace.id, marketplace.active)}
                            disabled={vlFeeReadOnly || !!toggleLoading[marketplace.id] || marketplace.id.startsWith("static:")}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: "#5D75F8",
                                "& + .MuiSwitch-track": {
                                  backgroundColor: "#5D75F8",
                                },
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell className={styles.tableCell} align="right">
                          <span className={styles.qaNetsValue}>
                            {computeQaNetLabel(localFees[marketplace.id] ?? getNumericFee(marketplace.vl_fee_percentage, 0))}
                          </span>
                        </TableCell>
                        {/* <TableCell className={styles.tableCell} align="center">
                          <button
                            className={styles.deleteButton}
                            onClick={() => openDeleteConfirm(marketplace.id, marketplace.name)}
                            disabled={vlFeeReadOnly || marketplace.id.startsWith("static:")}
                            title={marketplace.id.startsWith("static:") ? "Default marketplace cannot be deleted" : "Delete marketplace"}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 6h18"></path>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              <line x1="10" y1="11" x2="10" y2="17"></line>
                              <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                          </button>
                        </TableCell> */}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Add New Marketplace Section */}
          <div className={styles.addMarketplace}>
            <div className={styles.addMarketplaceLeft}>
              <div className={styles.addMarketplaceLabel}>+ Add New Marketplace</div>
              <input
                type="text"
                placeholder="Marketplace Name e.g., MegaSeats"
                value={newMarketplaceName}
                onChange={(e) => setNewMarketplaceName(e.target.value)}
                className={styles.marketplaceNameInput}
                disabled={vlFeeReadOnly}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddMarketplace();
                  }
                }}
              />
            </div>
            <div className={styles.addMarketplaceRight}>
              <div className={styles.feeLabel}>VL Fee %</div>
              <div className={styles.feeInputGroup}>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={newMarketplaceFee}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    if (!isNaN(val) && val >= 0) {
                      setNewMarketplaceFee(val);
                    }
                  }}
                  className={styles.feeSmallInput}
                  disabled={vlFeeReadOnly}
                  onKeyDown={(e) => {
                    if (["-", "+", "e", "E"].includes(e.key)) {
                      e.preventDefault();
                    }
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddMarketplace();
                    }
                  }}
                />
                <span className={styles.percentSymbol}>%</span>
                <button 
                  className={styles.addButton} 
                  onClick={handleAddMarketplace} 
                  disabled={vlFeeReadOnly || addingMarketplace || !newMarketplaceName.trim()}
                >
                  {addingMarketplace ? 'Adding...' : '+ Add'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {/* {deleteConfirm.isOpen && (
        <div className={styles.modalOverlay} onClick={() => setDeleteConfirm({ isOpen: false, marketplaceId: null, marketplaceName: "" })}>
          <div className={styles.deleteModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.deleteModalIcon}>
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <h3 className={styles.deleteModalTitle}>Delete Marketplace?</h3>
            <p className={styles.deleteModalMessage}>
              Are you sure you want to delete <strong>"{deleteConfirm.marketplaceName}"</strong>? 
              <br />
              This action cannot be undone.
            </p>
            <div className={styles.deleteModalActions}>
              <button 
                className={styles.deleteModalCancel} 
                onClick={() => setDeleteConfirm({ isOpen: false, marketplaceId: null, marketplaceName: "" })}
              >
                Cancel
              </button>
              <button 
                className={styles.deleteModalConfirm} 
                onClick={handleDeleteMarketplace}
              >
                Delete Marketplace
              </button>
            </div>
          </div>
        </div>
      )} */}

      {/* SECTION 2: Email Campaigns */}
      {selectedSection === "emailCampaigns" && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"></path>
              <rect x="2" y="4" width="20" height="16" rx="2"></rect>
            </svg>
            <span>Email Campaigns</span>
          </div>

          <div className={styles.emailLayout}>
            {/* Left Column - Compose */}
            <div className={styles.composeColumn}>
              <div className={styles.templateSection}>
                <div className={styles.templateLabel}>Email Template</div>
                <div className={styles.templateButtons}>
                  <button
                    className={`${styles.templateBtn} ${selectedTemplate === "default" ? styles.templateBtnActive : ""}`}
                    style={{ backgroundColor: selectedTemplate === "default" ? "#5D75F8" : "#f0f0f0" }}
                    onClick={() => setSelectedTemplate("default")}
                  >
                    Default
                  </button>
                  <button
                    className={`${styles.templateBtn} ${selectedTemplate === "announcement" ? styles.templateBtnActive : ""}`}
                    style={{ backgroundColor: selectedTemplate === "announcement" ? "#10B981" : "#f0f0f0" }}
                    onClick={() => setSelectedTemplate("announcement")}
                  >
                    Announcement
                  </button>
                  <button
                    className={`${styles.templateBtn} ${selectedTemplate === "promotion" ? styles.templateBtnActive : ""}`}
                    style={{ backgroundColor: selectedTemplate === "promotion" ? "#F59E0B" : "#f0f0f0" }}
                    onClick={() => setSelectedTemplate("promotion")}
                  >
                    Promotion
                  </button>
                  <button
                    className={`${styles.templateBtn} ${selectedTemplate === "newsletter" ? styles.templateBtnActive : ""}`}
                    style={{ backgroundColor: selectedTemplate === "newsletter" ? "#8B5CF6" : "#f0f0f0" }}
                    onClick={() => setSelectedTemplate("newsletter")}
                  >
                    Newsletter
                  </button>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Subject</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className={styles.formInput}
                  placeholder="Email subject line..."
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Body</label>
                <textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  className={styles.formTextarea}
                  placeholder="Write your email message here..."
                />
              </div>

              <div className={styles.formGroup}>
                <div className={styles.imageUploadRow}>
                  <div className={styles.imageUploadControl}>
                    <label className={styles.imageUploadLabel}>Header image</label>
                    <input
                      ref={headerImageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleHeaderImageChange}
                      className={styles.imageUploadInput}
                    />
                    {headerImagePreview && (
                      <img
                        src={headerImagePreview}
                        alt="Header preview"
                        className={styles.imageUploadThumbnail}
                      />
                    )}
                  </div>
                  <div className={styles.imageUploadControl}>
                    <label className={styles.imageUploadLabel}>Footer image</label>
                    <input
                      ref={footerImageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFooterImageChange}
                      className={styles.imageUploadInput}
                    />
                    {footerImagePreview && (
                      <img
                        src={footerImagePreview}
                        alt="Footer preview"
                        className={styles.imageUploadThumbnail}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.recipientsSection}>
                <div className={styles.recipientsLabel}>Recipients</div>
                <div className={styles.radioGroup}>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="recipients"
                      checked={emailRecipient === "all"}
                      onChange={() => setEmailRecipient("all")}
                    />
                    All Active Users ({isCampaignUsersLoading ? "..." : activeUsersCount})
                  </label>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="recipients"
                      checked={emailRecipient === "select"}
                      onChange={() => setEmailRecipient("select")}
                    />
                    Select Users
                  </label>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="recipients"
                      checked={emailRecipient === "filter"}
                      onChange={() => setEmailRecipient("filter")}
                    />
                    Filter Users
                  </label>
                </div>
              </div>

              {emailRecipient === "filter" && (
                <CampaignRecipientFilterPanel
                  filters={recipientFilters}
                  onChange={setRecipientFilters}
                  teamsList={campaignTeamsList}
                  teamsLoading={campaignTeamsLoading}
                  matchingCount={filtersApplied ? filterResolvedUsers.length : null}
                  eligibleEmailCount={filtersApplied ? filterEligibleEmailUsers.length : null}
                  eligiblePushCount={filtersApplied ? filterEligiblePushUsers.length : null}
                  isResolving={isResolvingFilterUsers}
                  resolveError={filterResolveError}
                  onApplyFilters={resolveCampaignFilterUsers}
                  onClearFilters={clearRecipientFilters}
                  channel="email"
                />
              )}

              {emailRecipient === "filter" && filtersApplied && filterResolvedUsers.length > 0 && (
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Matching users</label>
                  <div className={styles.selectedUsersChips}>
                    {filterResolvedUsers.slice(0, 25).map((user) => (
                      <span key={user.u_id} className={styles.userChip}>
                        {getCampaignUserLabel(user)} ({user.u_email_id})
                        {user.u_email_enabled === false ? " · Disabled" : ""}
                      </span>
                    ))}
                    {filterResolvedUsers.length > 25 && (
                      <span className={styles.userChip}>+{filterResolvedUsers.length - 25} more</span>
                    )}
                  </div>
                </div>
              )}

              {emailRecipient === "select" && (
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Select Users</label>
                  <input
                    type="text"
                    value={userSearchText}
                    onChange={(e) => setUserSearchText(e.target.value)}
                    className={styles.formInput}
                    placeholder="Type name or email to search users..."
                  />
                  {selectedUsers.length > 0 && (
                    <div className={styles.selectedUsersChips}>
                      {selectedUsers.map((user) => (
                        <button
                          key={user.u_email_id}
                          type="button"
                          className={styles.userChip}
                          onClick={() => handleRemoveSelectedUser(user.u_email_id)}
                        >
                          {getCampaignUserLabel(user)} ({user.u_email_id}) ✕
                        </button>
                      ))}
                    </div>
                  )}
                  {disabledEmailScopedCount > 0 && (
                    <div className={styles.infoText} style={{ margin: "8px 0", color: "#b91c1c" }}>
                      {disabledEmailScopedCount} user(s) have disabled and cannot receive emails.
                    </div>
                  )}
                  <div className={styles.userSuggestionList}>
                    {isCampaignUsersLoading ? (
                      <div className={styles.userSuggestionItem}>Loading users...</div>
                    ) : campaignUsers.length > 0 ? (
                      campaignUsers.map((user) => (
                        <button
                          key={user.u_id}
                          type="button"
                          className={styles.userSuggestionItem}
                          onClick={() => handleSelectUser(user)}
                          disabled={user.u_email_enabled === false}
                          style={user.u_email_enabled === false ? { opacity: 0.55, cursor: "not-allowed" } : undefined}
                        >
                          <span>{getCampaignUserLabel(user)}</span>
                          <span className={styles.userSuggestionEmail}>
                            {user.u_email_id}
                            {user.u_email_enabled === false ? " ·  Disabled" : ""}
                          </span>
                        </button>
                      ))
                    ) : (
                      <div className={styles.userSuggestionItem}>No users found.</div>
                    )}
                  </div>
                </div>
              )}

              <div className={styles.actionButtons}>
                <div className={styles.sendModeGroup}>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="email-send-mode"
                      checked={emailSendMode === "now"}
                      onChange={() => setEmailSendMode("now")}
                    />
                    Send Now
                  </label>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="email-send-mode"
                      checked={emailSendMode === "schedule"}
                      onChange={() => setEmailSendMode("schedule")}
                    />
                    Schedule
                  </label>
                </div>
                {emailSendMode === "schedule" && (
                  <input
                    type="datetime-local"
                    className={styles.scheduleInput}
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    min={getMinDateTimeLocalInputValue()}
                  />
                )}
                <button className={styles.previewBtn} onClick={handlePreviewEmail}>Preview Email</button>
                <button className={styles.sendBtn} onClick={handleSendEmailCampaign} disabled={isSendingEmail}>
                  {isSendingEmail ? (emailSendMode === "schedule" ? "Scheduling..." : "Sending...") : (emailSendMode === "schedule" ? "Schedule Email" : "Send Email")}
                </button>
              </div>
            </div>

            {/* Right Column - Preview */}
            <div className={styles.previewColumn}>
              <div className={styles.emailPreview}>
                <div
                  className={styles.previewHeader}
                  style={{ backgroundColor: templateColors[selectedTemplate] }}
                >
                  {emailSubject || "Email Subject"}
                </div>
                <div className={styles.previewContent}>
                  <div
                    className={styles.previewBody}
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Email History Section */}
          <div className={styles.historySection}>
            <div className={styles.historyHeader}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span>Email History</span>
            </div>
            <p className={styles.historySubtitle}>
              Track queued and delivered campaigns
            </p>
            <div className={styles.historyTabs}>
              <button
                type="button"
                className={`${styles.historyTabBtn} ${historyTab === "scheduled" ? styles.historyTabActive : ""}`}
                onClick={() => setHistoryTab("scheduled")}
              >
                Scheduled Emails
              </button>
              <button
                type="button"
                className={`${styles.historyTabBtn} ${historyTab === "sent" ? styles.historyTabActive : ""}`}
                onClick={() => setHistoryTab("sent")}
              >
                Sent Emails
              </button>
            </div>
            {historyLoading ? (
              <div className={styles.emptyState}>Loading email history...</div>
            ) : historyTab === "scheduled" ? (
              filteredScheduledCampaigns.length === 0 ? (
                <div className={styles.emptyState}>No {selectedTemplate} scheduled emails</div>
              ) : (
                <div className={styles.historyList}>
                  {filteredScheduledCampaigns.map((item) => (
                    <div key={item.ecs_id} className={styles.historyItem}>
                      <div className={styles.historyItemTop}>
                        <span className={styles.historySubject}>{item.ecs_subject}</span>
                        <span className={styles.historyDate}>{new Date(item.ecs_scheduled_at).toLocaleString()}</span>
                      </div>
                      <div className={styles.historyMeta}>
                        Template: {formatHistoryTemplateName(item.ecs_template_type)} | Recipients: {formatHistoryRecipientsLabel(item.ecs_recipient_type)} | Status: {item.ecs_status}
                        {item.ecs_error ? ` | Error: ${item.ecs_error}` : ""}
                      </div>
                      {item.ecs_status === "pending" && (
                        <div className={styles.historyActions}>
                          <button className={styles.smallActionBtn} onClick={() => openPreviewScheduled(item)}>
                            Preview
                          </button>
                          <button className={styles.smallActionBtn} onClick={() => openEditScheduledCampaign(item)}>
                            Edit
                          </button>
                          <button className={styles.smallDangerBtn} onClick={() => handleDeleteScheduledCampaign(item.ecs_id)}>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )
            ) : filteredCampaignHistory.length === 0 ? (
              <div className={styles.emptyState}>No {selectedTemplate} emails sent yet</div>
            ) : (
              <div className={styles.historyList}>
                {filteredCampaignHistory.map((item) => (
                  <div key={item.ech_id} className={styles.historyItem}>
                    <div className={styles.historyItemTop}>
                      <span className={styles.historySubject}>{item.ech_subject}</span>
                      <span className={styles.historyDate}>{new Date(item.ech_created_at).toLocaleString()}</span>
                    </div>
                    <div className={styles.historyMeta}>
                      Template: {formatHistoryTemplateName(item.ech_template_type)} | 
                      Recipients: {formatHistoryRecipientsLabel(item.ech_recipient_type)} | 
                      Delivered: {item.ech_sent_successfully} of {item.ech_total_recipients}
                      {item.ech_failed > 0 ? ` | Failed: ${item.ech_failed}` : ""}
                    </div>
                    <div className={styles.historyActions}>
                      <button className={styles.smallActionBtn} onClick={() => openPreviewSent(item)}>
                        Preview
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {selectedSection === "pushNotifications" && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
            <span>Push Notifications</span>
          </div>
          <p className={styles.sectionDescription}>
            Create, schedule, pause, resume, and delete app push notification campaigns.
          </p>

          <div className={styles.emailLayout}>
            <div className={styles.composeColumn}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Notification Title</label>
                <input
                  type="text"
                  value={pushTitle}
                  onChange={(e) => setPushTitle(e.target.value)}
                  className={styles.formInput}
                  placeholder="Push notification title..."
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Message Body</label>
                <textarea
                  value={pushBody}
                  onChange={(e) => setPushBody(e.target.value)}
                  className={styles.formTextarea}
                  placeholder="Write the push notification message..."
                />
              </div>

              <div className={styles.recipientsSection}>
                <div className={styles.recipientsLabel}>Recipients</div>
                <div className={styles.radioGroup}>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="push-recipients"
                      checked={pushRecipient === "all"}
                      onChange={() => setPushRecipient("all")}
                    />
                    All Push-Enabled Users ({isCampaignUsersLoading ? "..." : activePushUsersCount})
                  </label>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="push-recipients"
                      checked={pushRecipient === "select"}
                      onChange={() => setPushRecipient("select")}
                    />
                    Select Users
                  </label>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="push-recipients"
                      checked={pushRecipient === "filter"}
                      onChange={() => setPushRecipient("filter")}
                    />
                    Filter Users
                  </label>
                </div>
              </div>

              {pushRecipient === "filter" && (
                <CampaignRecipientFilterPanel
                  filters={recipientFilters}
                  onChange={setRecipientFilters}
                  teamsList={campaignTeamsList}
                  teamsLoading={campaignTeamsLoading}
                  matchingCount={filtersApplied ? filterResolvedUsers.length : null}
                  eligibleEmailCount={filtersApplied ? filterEligibleEmailUsers.length : null}
                  eligiblePushCount={filtersApplied ? filterEligiblePushUsers.length : null}
                  isResolving={isResolvingFilterUsers}
                  resolveError={filterResolveError}
                  onApplyFilters={resolveCampaignFilterUsers}
                  onClearFilters={clearRecipientFilters}
                  channel="push"
                />
              )}

              {pushRecipient === "filter" && filtersApplied && filterResolvedUsers.length > 0 && (
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Matching users</label>
                  <div className={styles.selectedUsersChips}>
                    {filterResolvedUsers.slice(0, 25).map((user) => (
                      <span key={user.u_id} className={styles.userChip}>
                        {getCampaignUserLabel(user)} ({user.u_email_id})
                      </span>
                    ))}
                    {filterResolvedUsers.length > 25 && (
                      <span className={styles.userChip}>+{filterResolvedUsers.length - 25} more</span>
                    )}
                  </div>
                </div>
              )}

              {pushRecipient === "select" && (
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Select Push-Enabled Users</label>
                  <input
                    type="text"
                    value={pushUserSearchText}
                    onChange={(e) => setPushUserSearchText(e.target.value)}
                    className={styles.formInput}
                    placeholder="Type name or email to search users..."
                  />
                  {selectedPushUsers.length > 0 && (
                    <div className={styles.selectedUsersChips}>
                      {selectedPushUsers.map((user) => (
                        <button
                          key={user.u_id}
                          type="button"
                          className={styles.userChip}
                          onClick={() => handleRemoveSelectedPushUser(user.u_id)}
                        >
                          {getCampaignUserLabel(user)} ({user.u_email_id}) ✕
                        </button>
                      ))}
                    </div>
                  )}
                  <div className={styles.userSuggestionList}>
                    {isCampaignUsersLoading ? (
                      <div className={styles.userSuggestionItem}>Loading users...</div>
                    ) : pushCampaignUsers.length > 0 ? (
                      pushCampaignUsers.map((user) => (
                        <button
                          key={user.u_id}
                          type="button"
                          className={styles.userSuggestionItem}
                          onClick={() => handleSelectPushUser(user)}
                        >
                          <span>{getCampaignUserLabel(user)}</span>
                          <span className={styles.userSuggestionEmail}>{user.u_email_id}</span>
                        </button>
                      ))
                    ) : (
                      <div className={styles.userSuggestionItem}>No push-enabled users found.</div>
                    )}
                  </div>
                </div>
              )}

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Cadence / Frequency</label>
                <select className={styles.formInput} value={pushFrequency} onChange={(e) => setPushFrequency(e.target.value as PushFrequency)}>
                  <option value="one_time">One-time</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Scheduled Send Time</label>
                <input
                  type="datetime-local"
                  className={styles.scheduleInput}
                  value={pushScheduledAt}
                  onChange={(e) => setPushScheduledAt(e.target.value)}
                />
                <p className={styles.historySubtitle}>
                  Leave empty to send immediately when activated.
                </p>
              </div>

              <div className={styles.actionButtons}>
                <button className={styles.previewBtn} onClick={() => handleSavePushCampaign("draft")} disabled={isSavingPush}>
                  Save Draft
                </button>
                <button className={styles.sendBtn} onClick={() => handleSavePushCampaign("active")} disabled={isSavingPush}>
                  {isSavingPush ? "Saving..." : pushScheduledAt ? "Schedule / Activate" : "Send / Activate"}
                </button>
              </div>
            </div>

            <div className={styles.previewColumn}>
              <div className={styles.emailPreview}>
                <div className={styles.previewHeader}>
                  {pushTitle || "Push Notification Title"}
                </div>
                <div className={styles.previewContent}>
                  <div className={styles.previewBody}>
                    {pushBody || "Your push notification message will appear here..."}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.historySection}>
            <div className={styles.historyHeader}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 8v4l3 3"></path>
                <circle cx="12" cy="12" r="10"></circle>
              </svg>
              <span>Push Notification Campaigns</span>
            </div>
            <p className={styles.historySubtitle}>
              Centralized view of all push notifications, schedules, cadence, and delivery status.
            </p>
            <div className={styles.historyTabs}>
              {(["all", "draft", "active", "paused", "sent", "failed"] as const).map((status) => (
                <button
                  key={status}
                  type="button"
                  className={`${styles.historyTabBtn} ${pushStatusFilter === status ? styles.historyTabActive : ""}`}
                  onClick={() => setPushStatusFilter(status)}
                >
                  {status === "all" ? "All" : status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>

            {pushLoading ? (
              <div className={styles.emptyState}>Loading push notification campaigns...</div>
            ) : filteredPushCampaigns.length === 0 ? (
              <div className={styles.emptyState}>No push notification campaigns found.</div>
            ) : (
              <div className={styles.historyList}>
                {filteredPushCampaigns.map((campaign) => (
                  <div key={campaign.pnc_id} className={styles.historyItem}>
                    <div className={styles.historyItemTop}>
                      <span className={styles.historySubject}>{campaign.pnc_title}</span>
                      <span className={styles.historyDate}>
                        {campaign.pnc_next_run_at
                          ? `Next: ${new Date(campaign.pnc_next_run_at).toLocaleString()}`
                          : campaign.pnc_last_sent_at
                          ? `Last sent: ${new Date(campaign.pnc_last_sent_at).toLocaleString()}`
                          : `Created: ${new Date(campaign.pnc_created_at).toLocaleString()}`}
                      </span>
                    </div>
                    <div className={styles.historyMeta}>
                      Status: {campaign.pnc_status} | Cadence: {campaign.pnc_frequency.replace("_", " ")} | Recipients: {formatHistoryRecipientsLabel(campaign.pnc_recipient_type)} | Sent: {campaign.pnc_sent_successfully} of {campaign.pnc_total_recipients}
                      {campaign.pnc_failed > 0 ? ` | Failed: ${campaign.pnc_failed}` : ""}
                      {campaign.pnc_error ? ` | Error: ${campaign.pnc_error}` : ""}
                    </div>
                    <div className={styles.previewModalMeta}>{campaign.pnc_body}</div>
                    <div className={styles.historyActions}>
                      <button className={styles.smallActionBtn} onClick={() => setPreviewPushCampaign(campaign)}>
                        Preview
                      </button>
                      {["draft", "active", "paused", "failed"].includes(campaign.pnc_status) && (
                        <button className={styles.smallActionBtn} onClick={() => openEditPushCampaign(campaign)}>
                          Edit
                        </button>
                      )}
                      {campaign.pnc_status === "active" && (
                        <button className={styles.smallActionBtn} onClick={() => handleUpdatePushCampaignStatus(campaign, "paused")}>
                          Pause
                        </button>
                      )}
                      {(campaign.pnc_status === "paused" || campaign.pnc_status === "draft") && (
                        <button className={styles.smallActionBtn} onClick={() => handleUpdatePushCampaignStatus(campaign, "active")}>
                          Resume
                        </button>
                      )}
                      {campaign.pnc_status !== "processing" && (
                        <button className={styles.smallDangerBtn} onClick={() => handleDeletePushCampaign(campaign.pnc_id)}>
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {(previewSentItem || previewScheduledItem) && (
        <div className={styles.previewModalOverlay} onClick={closePreview}>
          <div className={styles.previewModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.previewModalHeader}>
              Email Preview
              <button onClick={closePreview} style={{ float: 'right', background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
            </div>

            {/* Metadata */}
            <div className={styles.previewModalMeta}>
              <strong>Subject:</strong> {previewSentItem?.ech_subject || previewScheduledItem?.ecs_subject}
            </div>
            <div className={styles.previewModalMeta}>
              <strong>Template:</strong> {formatHistoryTemplateName(previewSentItem?.ech_template_type || previewScheduledItem?.ecs_template_type)}
            </div>
            <div className={styles.previewModalMeta}>
              <strong>Recipients:</strong> {formatHistoryRecipientsLabel(previewSentItem?.ech_recipient_type || previewScheduledItem?.ecs_recipient_type)}
            </div>
            <div className={styles.previewModalMeta}>
              <strong>Date:</strong> {new Date(previewSentItem?.ech_created_at || previewScheduledItem?.ecs_scheduled_at || "").toLocaleString()}
            </div>
            {previewSentItem?.ech_sent_successfully !== undefined && (
              <div className={styles.previewModalMeta}>
                <strong>Delivered:</strong> {previewSentItem.ech_sent_successfully} of {previewSentItem.ech_total_recipients}
              </div>
            )}

            {/* Email Body */}
            <div className={styles.previewModalSubject}>
              {previewSentItem?.ech_subject || previewScheduledItem?.ecs_subject}
            </div>
            <div
              className={styles.previewModalBody}
              dangerouslySetInnerHTML={{
                __html: previewSentItem?.ech_body
                  ? buildCampaignEmailHtml(
                      previewSentItem.ech_body,
                      previewSentItem.ech_header_image,
                      previewSentItem.ech_footer_image,
                    )
                  : previewScheduledItem?.ecs_body
                  ? buildCampaignEmailHtml(
                      previewScheduledItem.ecs_body,
                      previewScheduledItem.ecs_header_image,
                      previewScheduledItem.ecs_footer_image,
                    )
                  : "<p><em>Email content not available.</em></p>"
              }}
            />

            <div className={styles.actionButtons}>
              <button className={styles.previewBtn} onClick={closePreview}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {isPreviewOpen && (
        <div className={styles.previewModalOverlay} onClick={() => setIsPreviewOpen(false)}>
          <div className={styles.previewModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.previewModalHeader}>Email Preview</div>
            <div className={styles.previewModalMeta}>
              Recipients:{" "}
              {emailRecipient === "all"
                ? "All active users"
                : emailRecipient === "filter"
                  ? `${filterEligibleEmailUsers.length} filtered user(s)`
                  : `${selectedUsers.length}`}
            </div>
            {emailRecipient === "select" && selectedUsers.length > 0 && (
              <div className={styles.previewModalMeta}>
                Users: {selectedUsers
                  .map((user) => getCampaignUserLabel(user))
                  .join(", ")}
              </div>
            )}
            <div className={styles.previewModalSubject}>{emailSubject}</div>
            <div
              className={styles.previewModalBody}
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
            <button className={styles.sendBtn} onClick={() => setIsPreviewOpen(false)}>Close Preview</button>
          </div>
        </div>
      )}
      {editingCampaign && (
        <div className={styles.previewModalOverlay} onClick={() => setEditingCampaign(null)}>
          <div className={styles.previewModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.previewModalHeader}>Edit Scheduled Email</div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Subject</label>
              <input className={styles.formInput} value={editSubject} onChange={(e) => setEditSubject(e.target.value)} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Body</label>
              <textarea className={styles.formTextarea} value={editBody} onChange={(e) => setEditBody(e.target.value)} />
            </div>
            <div className={styles.formGroup}>
              <div className={styles.imageUploadRow}>
                <div className={styles.imageUploadControl}>
                  <label className={styles.imageUploadLabel}>Header image</label>
                  <input
                    ref={editHeaderImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleEditHeaderImageChange}
                    className={styles.imageUploadInput}
                  />
                  {editHeaderImagePreview && (
                    <img
                      src={editHeaderImagePreview}
                      alt="Header preview"
                      className={styles.imageUploadThumbnail}
                    />
                  )}
                </div>
                <div className={styles.imageUploadControl}>
                  <label className={styles.imageUploadLabel}>Footer image</label>
                  <input
                    ref={editFooterImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleEditFooterImageChange}
                    className={styles.imageUploadInput}
                  />
                  {editFooterImagePreview && (
                    <img
                      src={editFooterImagePreview}
                      alt="Footer preview"
                      className={styles.imageUploadThumbnail}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Template</label>
              <select className={styles.formInput} value={editTemplate} onChange={(e) => setEditTemplate(e.target.value as any)}>
                <option value="default">Default</option>
                <option value="announcement">Announcement</option>
                <option value="promotion">Promotion</option>
                <option value="newsletter">Newsletter</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Scheduled At</label>
              <input
                type="datetime-local"
                className={styles.scheduleInput}
                value={editScheduledAt}
                onChange={(e) => setEditScheduledAt(e.target.value)}
                min={getMinDateTimeLocalInputValue()}
              />
            </div>
            <div className={styles.actionButtons}>
              <button className={styles.previewBtn} onClick={() => setEditingCampaign(null)}>Cancel</button>
              <button className={styles.sendBtn} onClick={handleUpdateScheduledCampaign}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
      {previewPushCampaign && (
        <div className={styles.previewModalOverlay} onClick={() => setPreviewPushCampaign(null)}>
          <div className={styles.previewModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.previewModalHeader}>
              Push Notification Preview
              <button onClick={() => setPreviewPushCampaign(null)} style={{ float: 'right', background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
            </div>
            <div className={styles.previewModalMeta}>
              <strong>Status:</strong> {previewPushCampaign.pnc_status}
            </div>
            <div className={styles.previewModalMeta}>
              <strong>Cadence:</strong> {previewPushCampaign.pnc_frequency.replace("_", " ")}
            </div>
            <div className={styles.previewModalMeta}>
              <strong>Recipients:</strong> {formatHistoryRecipientsLabel(previewPushCampaign.pnc_recipient_type)}
            </div>
            {previewPushCampaign.pnc_scheduled_at && (
              <div className={styles.previewModalMeta}>
                <strong>Scheduled:</strong> {new Date(previewPushCampaign.pnc_scheduled_at).toLocaleString()}
              </div>
            )}
            {previewPushCampaign.pnc_next_run_at && (
              <div className={styles.previewModalMeta}>
                <strong>Next Send:</strong> {new Date(previewPushCampaign.pnc_next_run_at).toLocaleString()}
              </div>
            )}
            {previewPushCampaign.pnc_last_sent_at && (
              <div className={styles.previewModalMeta}>
                <strong>Last Sent:</strong> {new Date(previewPushCampaign.pnc_last_sent_at).toLocaleString()}
              </div>
            )}
            <div className={styles.previewModalMeta}>
              <strong>Delivery:</strong> Sent {previewPushCampaign.pnc_sent_successfully} of {previewPushCampaign.pnc_total_recipients}
              {previewPushCampaign.pnc_failed > 0 ? ` | Failed: ${previewPushCampaign.pnc_failed}` : ""}
            </div>
            {previewPushCampaign.pnc_error && (
              <div className={styles.previewModalMeta}>
                <strong>Error:</strong> {previewPushCampaign.pnc_error}
              </div>
            )}
            <div className={styles.previewModalSubject}>{previewPushCampaign.pnc_title}</div>
            <div className={styles.previewModalBody}>
              {previewPushCampaign.pnc_body || "Push notification message not available."}
            </div>
            <div className={styles.actionButtons}>
              <button className={styles.previewBtn} onClick={() => setPreviewPushCampaign(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
      {editingPushCampaign && (
        <div className={styles.previewModalOverlay} onClick={() => setEditingPushCampaign(null)}>
          <div className={styles.previewModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.previewModalHeader}>Edit Push Notification</div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Notification Title</label>
              <input className={styles.formInput} value={editPushTitle} onChange={(e) => setEditPushTitle(e.target.value)} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Message Body</label>
              <textarea className={styles.formTextarea} value={editPushBody} onChange={(e) => setEditPushBody(e.target.value)} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Cadence / Frequency</label>
              <select className={styles.formInput} value={editPushFrequency} onChange={(e) => setEditPushFrequency(e.target.value as PushFrequency)}>
                <option value="one_time">One-time</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Status</label>
              <select className={styles.formInput} value={editPushStatus} onChange={(e) => setEditPushStatus(e.target.value as "draft" | "active" | "paused")}>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Scheduled Send Time</label>
              <input
                type="datetime-local"
                className={styles.scheduleInput}
                value={editPushScheduledAt}
                onChange={(e) => setEditPushScheduledAt(e.target.value)}
              />
            </div>
            <div className={styles.actionButtons}>
              <button className={styles.previewBtn} onClick={() => setEditingPushCampaign(null)}>Cancel</button>
              <button className={styles.sendBtn} onClick={handleUpdatePushCampaign}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;